import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { Mongo } from 'meteor/mongo';
import { strings } from '/imports/strings';
import { objectValues, randomString, referenceString } from '/imports/utils';
import { MethodDeclaration } from '/imports/methods';
import { Dict, Docmnt, ListMethodArg, ListMethodResponse, ModelClass } from '/imports/types';
import { css } from '/imports/ui/css';
import { Icon } from '/imports/ui/Icon';
import { Input } from '/imports/ui/Input';
import { LoadingFill } from '/imports/ui/LoadingFill';
import { isIos } from '/imports/ios';

// isIos check may need to cover mobile safari too (consider useragent)
const RichSelectStyled = styled.div`
  ${!isIos() ?
    `
      ${css.mobileMedia} {
        &.open {
          position: fixed;
          top: 1rem;
          right: 1rem;
          width: calc(100% - 2rem);
          height: calc(100% - 3rem);
          z-index: 3;
          .mobile-underlay {
            display: block;
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: ${css.primaryColorTransparent};
            z-index: 3;
          }
          .input, .options {
            z-index: 4;
          }
        }
      }
      `
    :
    ''
}
  &.has-value {
    input::placeholder {
      color: ${css.grey2};
      font-style: normal;
    }
  }
  position: relative;
  width: 100%;
  input {
    width: 100%;
  }
  .rich-select-options-header, .rich-select-options-footer {
    color: ${css.primaryColor};
    padding: 0.5rem;
    cursor: pointer;
    font-weight: bold;
  }
  .options {
    position: absolute;
    display: none;
    color: ${css.grey2};
    background: #FFF;
    border: ${css.inputBorder};
    border-radius: ${css.inputBorderRadius};
    width: 100%;
    z-index: 1;
    overflow: auto;
    &::-webkit-scrollbar {
      width: 0;
      height: 0;
    }
    &::-webkit-scrollbar-track {
      background: red;
    }
    &::-webkit-scrollbar-thumb {
      background: red;
    }
    &::-webkit-scrollbar-thumb:hover {
      background: red;
    }
    &.open {
      display: block;
    }
  }
  .option {
    padding: 1rem 0.5rem;
    cursor: pointer;
    &:hover {
      color: ${css.primaryColor};
    }
  }
  .no-results {
    text-transform: capitalize;
    min-height: 3rem;
    display: flex;
    flex-direction: column;
    justify-content: space-around;
    text-align: center;
  }
  .loading {
    height: 4rem;
  }
  &.error {
    input {
      border: 1px solid ${css.red2};
    }
  }
  .mobile-underlay {
    display: none;
  }
  .clear {
    position: absolute;
    right: 0.75rem;
    top: 0.75rem;
  }
  .rich-select-search {
    .clear {
      display: none;
    }
  }
`;

export interface Option {
  data?: any,
  value: string,
  label: string,
  render?: (option: Option) => React.ReactNode,
  clear?: boolean,
}

export interface RichSelectProps<T extends Docmnt = any, S extends Docmnt = any> {
  id?: string;
  className?: string;
  placeholder?: string;
  options?: Option[]
  listMethod?: MethodDeclaration<ListMethodArg<T>, ListMethodResponse<T>>;
  listQuery?: Mongo.Query<T> | (() => Mongo.Query<T>);
  mapToValue?(obj: T): Option;
  loading?: boolean;
  value?: string;
  preSelect?: {
    modelClass: ModelClass<T>;
    document: S;
    field: keyof T;
  };
  preSelectedOption?: Option;
  disabled?: boolean;
  defaultOpen?: boolean;
  optionsHeader?: React.ReactNode;
  optionsFooter?: React.ReactNode;
  clearable?: boolean;
  filter?(option: Option): boolean;
  onChange?(option: Option): void;
  onClear?(): void;
  onSearch?(search?: string, e?: React.ChangeEvent<HTMLInputElement>): void;
  onFocus?(e: React.FocusEvent<HTMLInputElement, Element>): void;
  onBlur?(e: React.FocusEvent<HTMLInputElement, Element>): void;
  selectionTrigger?(handler: (option: Option, triggerOnChange?: boolean) => void): void;
  fixedOptions?: boolean;
}

let handlingScrollBottom = false;

const _document = document;

/** Searchable select with optional async paged loading, pre-selection, and clear support. */
export const RichSelect = <T extends Docmnt = any>(props: RichSelectProps<T>) => {
  const { value, options, listMethod, mapToValue, loading, disabled, preSelect, preSelectedOption, optionsHeader, optionsFooter,
    filter, listQuery, clearable, fixedOptions } = props;
  const { modelClass, document, field } = preSelect || {};
  const defaultOption = modelClass && field && document?.[field] ?
    { value: document[field] as string, label: referenceString(modelClass, document, field as string) }
    :
    preSelectedOption;
  const [selectedOption, setSelectedOption] = useState<Option | undefined>(defaultOption);
  const [stateOptions, setStateOptions] = useState<Option[]>(options || (defaultOption && [defaultOption]) || []);
  const [listLoading, setListLoading] = useState(!!listMethod);
  const [search, setSearch] = useState<string>();
  const [open, setOpen] = useState(!!props.defaultOpen);
  const _listQuery = typeof listQuery === 'function' ? listQuery() : listQuery;
  const [methodArg, setMethodArg] = useState<ListMethodArg<T>>({ skip: 0, limit: 20, query: _listQuery });
  const [focusedOption,] = useState<any>();
  const [inputId,] = useState(randomString());
  const [optionsId,] = useState(randomString());
  const [scrollExhausted, setScrollExhausted] = useState(false);
  /**
   * Handles keyboard navigation within the search input.
   * @param e - keyboard event
   */
  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      if (focusedOption) {
        // ToDo;
      }
    }
  };
  /**
   * Programmatically selects an option (exposed via props.selectionTrigger).
   * @param option - option to select
   */
  const selectionTrigger = (option: Option) => {
    setSelectedOption(option);
  };
  useEffect(() => {
    props.selectionTrigger?.(selectionTrigger);
  }, []);
  useEffect(() => {
    if (!options) return;
    if (listLoading || !listMethod) {
      setStateOptions(mergeOptions([...stateOptions, ...options]));
      return;
    }
    if (listMethod && !listLoading) return;
  }, [options]);
  /**
   * Maps documents to options using mapToValue or default label/value resolution.
   * @param documents - documents to map
   * @returns mapped options
   */
  const mapOptions = (documents: T[]) => {
    return documents.map((obj) => mapToValue ?
      mapToValue(obj)
      :
      { value: obj._id, label: (obj as any)._name || modelClass?.asString?.(obj) || (obj as any).name, data: obj } as Option);
  };
  /**
   * Deduplicates options by value, keeping the last occurrence.
   * @param options - options to merge
   * @returns deduplicated options
   */
  const mergeOptions = (options: (Option | undefined)[]) => {
    // Tolerate holes: a lookup that found nothing must not take the whole select down.
    const _options = objectValues(options.filter((opt): opt is Option => !!opt).reduce((map: Dict<Option>, opt) => {
      map[opt.value] = opt;
      return map;
    }, {}));
    return _options;
  };
  useEffect(() => {
    if (!listMethod) return;
    (async () => {
      try {
        handlingScrollBottom = true;
        setListLoading(true);
        const response = await listMethod.call(methodArg);
        const options = mapOptions(response.documents);
        const newOptions = mergeOptions([...stateOptions, ...options]);
        setStateOptions(newOptions);
        !options.length && setScrollExhausted(true);
      }
      catch (error) {
        toast.error(`${error}`);
      }
      finally {
        handlingScrollBottom = false;
        setListLoading(false);
      }
    })();
  }, [listMethod, methodArg]);
  useEffect(() => {
    if (!listMethod || !value) return;
    (async () => {
      const response = await listMethod.call({ query: { _id: value as any }, limit: 1 });
      const option = mapOptions(response.documents)[0];
      // The value may point at a document the list method filters out (e.g. an account whose user has
      // since been deactivated — listActiveUsers won't return it). Keep whatever is already shown (the
      // pre-selected option) rather than selecting nothing and crashing on it.
      if (!option) return;
      const newOptions = mergeOptions([...stateOptions, option]);
      setStateOptions(newOptions);
      setSelectedOption({ ...option });
    })();
  }, [value]);
  useEffect(() => {
    if (listMethod) {
      setScrollExhausted(false);
      setStateOptions(defaultOption && [defaultOption] || []);
      setMethodArg({ ...methodArg, search, skip: 0, query: _listQuery });
    }
  }, [search, JSON.stringify(_listQuery || {})]);
  useEffect(() => {
    setSelectedOption(stateOptions?.filter((o) => o.value === value)[0] || selectedOption);
  }, [stateOptions]);
  /**
   * Loads the next page when the options list is scrolled near the bottom.
   * @param e - scroll event
   */
  const handleScroll = (e: React.UIEvent<HTMLDivElement, UIEvent>) => {
    if (scrollExhausted) return;
    const target = e.target as HTMLElement;
    const hitBottom = target.scrollHeight - target.scrollTop - 0.1 * target.scrollHeight <= target.clientHeight;
    if (hitBottom && !handlingScrollBottom) {
      handlingScrollBottom = true;
      setMethodArg({ ...methodArg, skip: stateOptions.length, query: _listQuery });
    }
  };
  /** Sizes and positions the options dropdown to fit within the viewport. */
  const setOptionsHeight = () => {
    const input = _document.getElementById(inputId);
    const options = _document.getElementById(optionsId);
    const inputRect = input?.getBoundingClientRect();
    const optionsRect = options?.getBoundingClientRect();
    const top = inputRect?.bottom;
    if (options && top) options.style.maxHeight = `${window.innerHeight - top - 16}px`;
    if (open && options && fixedOptions) {
      options.style.position = 'fixed';
      options.style.top = `${optionsRect?.top}px`;
      options.style.left = `${optionsRect?.left}px`;
      options.style.width = `${optionsRect?.width}px`;
    }
    else if (options && fixedOptions) {
      options.style.removeProperty('position');
      options.style.removeProperty('top');
      options.style.removeProperty('left');
      options.style.removeProperty('width');
    }
  };
  useEffect(() => {
    setOptionsHeight();
    const handler = () => setOptionsHeight();
    window.addEventListener('resize', handler);
    return () => { window.removeEventListener('resize', handler); };
  }, [open]);
  /**
   * Filters options by the current search text (used for client-side filtering).
   * @param option - option to test
   * @returns true if the option matches the search
   */
  const searchFilter = (option: Option) => {
    const _search = search?.trim().toLowerCase();
    if (!_search) return true;
    return option.label.toLowerCase().includes(_search);
  };
  return (
    <RichSelectStyled id={props.id} className={`${props.className}${open ? ' open' : ''}${selectedOption ? ' has-value' : ''}`} key={selectedOption?.value}>
      <div className="mobile-underlay" />
      <Input
        id={inputId}
        className="rich-select-search"
        placeholder={selectedOption?.label || props.placeholder || strings.Search()}
        value={search}
        disabled={disabled}
        onChange={(search, e) => { setSearch(search); props.onSearch?.(search, e); }}
        onFocus={(e) => { setOpen(true); props.onFocus?.(e); }}
        onBlur={(e) => { setOpen(false); setSearch(''); props.onBlur?.(e); }}
        onKeyPress={(e) => handleKeyPress(e)}
      />
      {clearable !== false && <Icon className="clear" icon="cancel" onClick={() => { setSelectedOption(undefined); props.onClear?.(); }} /> || null}
      <div className={`options${open ? ' open' : ''}`} id={optionsId} onScroll={handleScroll}>
        <div className="rich-select-options-header">{optionsHeader || null}</div>
        {
          stateOptions?.filter(searchFilter).filter(filter || Boolean).map((option) => {
            return (
              <div
                key={option.value}
                className="option"
                onMouseDown={() => { setSelectedOption(option); setOpen(false); option.clear ? props.onClear?.() : props.onChange?.(option); }}
              >
                {option.render?.(option) || option.label}
              </div>
            );
          })
        }
        {
          open && !loading && !listLoading && !stateOptions?.length ?
            (
              <div className="no-results">{strings.NoResults()}</div>
            )
            :
            null
        }
        {
          loading || listLoading ?
            (
              <div className="loading">
                <LoadingFill />
              </div>
            )
            :
            null
        }
        {/* A footer action must not steal focus: blurring the search input closes this panel, so an onClick
            in here never landed — the panel was already hidden by the time the click followed its mousedown.
            (The option rows above dodge the same race by acting on mousedown.) */}
        <div className="rich-select-options-footer" onMouseDown={(e) => e.preventDefault()}>{optionsFooter || null}</div>
      </div>
    </RichSelectStyled>
  );
};